"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { CopyIcon } from "@/components/icons/CopyIcon";
import { CheckIcon } from "@/components/icons/CheckIcon";

/** How long the button says "Copied". */
const COPIED_MS = 1500;

/**
 * Copy on a history message (spec 2026-09-24 §1). Revealed on hover of its
 * own turn (the nearest `group/turn`) where there is hover, always reachable
 * on touch. "Copied" only when the clipboard took the words: a refusal (no
 * permission, a frame without focus) leaves the button as it was.
 */
export function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // The clipboard refused; nothing was copied, so nothing is claimed.
    }
  };

  return (
    <button
      type="button"
      aria-label={copied ? "Copied" : label}
      title={copied ? undefined : "Copy"}
      onClick={() => void copy()}
      className={cn(
        "transition-bh-del relative grid h-5.5 w-6.5 flex-none cursor-pointer place-items-center rounded-6 border-0 bg-transparent p-0 text-fg-subtle opacity-0 hover:bg-bg-muted hover:text-fg-default focus-visible:opacity-100 group-hover/turn:opacity-100 [@media(hover:none)]:opacity-75",
        copied && "opacity-100"
      )}
    >
      {copied ? (
        <CheckIcon
          aria-hidden="true"
          strokeWidth={2.4}
          className="size-3.5 text-kind-green"
        />
      ) : (
        <CopyIcon className="size-3.5" />
      )}
      {copied && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded-6 bg-fg-default px-2 py-0.5 text-3 font-medium whitespace-nowrap text-bg-base"
        >
          Copied
        </span>
      )}
    </button>
  );
}
